import { motion } from "framer-motion";
import { ClipboardList, MonitorPlay, MessagesSquare, GraduationCap, Award, BookOpen, Check } from "lucide-react";

const steps = [
  { n: "01", icon: ClipboardList, title: "Inscription & accès immédiat", body: "Réservez votre place dans la première cohorte francophone et recevez vos accès à la plateforme." },
  { n: "02", icon: MonitorPlay, title: "E-learning & coaching en direct", body: "Modules interactifs combinés à des webinaires de coaching animés par des pairs aidants certifiés." },
  { n: "03", icon: MessagesSquare, title: "Forums de pratique", body: "Échangez avec une communauté mondiale de coaches et mettez en pratique en contexte réel." },
  { n: "04", icon: GraduationCap, title: "Examen final", body: "Validez vos compétences en accompagnement en Médecine Personnelle." },
  { n: "05", icon: Award, title: "Certification CPMC", body: "Obtenez la certification officielle Pat Deegan & Associates, reconnue à l'international." },
  { n: "06", icon: BookOpen, title: "Guide Personal Medicine", body: "Recevez le guide officiel et les versions PDF de tous les outils de la formation." },
];

const bonuses = [
  "19 semaines de formation — 12 semaines de curriculum original + 7 semaines bonifiées",
  "Modules sur la nutrition et la santé mentale animés par une accompagnatrice pair pour qui la nutrition fait partie intégrante de son plan de traitement depuis quelques années déjà",
  "Modules de pharmacologie pour un langage commun avec les équipes cliniques",
  "Modules d'accompagnement par les pairs animés par Luc Vigneault, premier pair-aidant certifié au Canada",
];

export default function Programme() {
  return (
    <section id="programme" className="bg-ink py-24 text-cream sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 max-w-3xl"
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-sun">
            Le parcours
          </p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Six étapes vers la{" "}
            <span className="text-outline-cream">certification</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-cream/70 sm:text-lg">
            Maîtrisez la puissance de la Médecine Personnelle grâce à une combinaison
            d'apprentissage en ligne, de coaching en webinaire et de pratique en contexte réel.
          </p>
        </motion.div>

        <div data-testid="cpmc-steps-container" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={`cpmc-step-card-${s.n}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6, scale: 1.02 }}
              className="group rounded-3xl border border-cream/10 bg-cream/[0.04] p-7 backdrop-blur transition-colors duration-300 hover:border-sun/40 hover:bg-cream/[0.07]"
            >
              <div className="mb-6 flex items-center justify-between">
                <span className="font-display text-3xl font-extrabold text-sun/90">{s.n}</span>
                <s.icon className="h-6 w-6 text-cream/40 transition-colors duration-300 group-hover:text-sun" />
              </div>
              <h3 className="mb-3 font-display text-lg font-bold leading-snug">{s.title}</h3>
              <p className="text-sm leading-relaxed text-cream/60">{s.body}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 rounded-3xl border border-sun/25 bg-gradient-to-br from-sun/15 to-blaze/10 p-8 sm:p-12"
        >
          <h3 className="mb-8 font-display text-2xl font-bold sm:text-3xl">
            Ce que vous obtenez
          </h3>
          <ul className="grid gap-5 md:grid-cols-2">
            {bonuses.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sun">
                  <Check className="h-3.5 w-3.5 text-ink" strokeWidth={3} />
                </span>
                <span className="text-sm leading-relaxed text-cream/85 sm:text-base">{b}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
